/**
 * Expandable Node Component
 *
 * Custom React Flow node for hierarchical evidence graphs.
 * Shows expand/collapse controls for nodes with children.
 * When expanded, renders InternalGraph showing contained sub-graph.
 * Visual: White card with colored badge, expand indicator for group nodes.
 */

import React, { memo, useState, useCallback, useRef } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  WorkspaceNodeData,
  getNodeTypeIcon,
  getNodeTypeColor,
  getNodeTypeLabel,
} from '../types';
import InternalGraph from './InternalGraph';
import { formatCount } from '../../../utils/plural';

// Extended data type for expandable nodes
export interface ExpandableNodeData extends WorkspaceNodeData {
  isExpanded?: boolean;
  onToggleExpand?: (nodeId: string) => void;
  childCount?: number;
  findingCount?: number;
  onFindingClick?: (findingId: string) => void;
}

function ExpandableNode({ id, data, selected }: NodeProps<ExpandableNodeData>) {
  const {
    nodeType,
    label,
    relevanceNote,
    referenceIds,
    children,
    childEdges,
    findingIds,
    isExpanded: externalExpanded = false,
    onToggleExpand,
    childCount,
    findingCount,
    onFindingClick,
    content,
  } = data;

  // Local expansion state - allows node to manage its own expanded state
  const [isLocalExpanded, setIsLocalExpanded] = useState(externalExpanded);
  const isExpanded = isLocalExpanded;

  // Resizable dimensions state
  const [dimensions, setDimensions] = useState({ width: 450, height: 350 });
  const isResizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const startDimensions = useRef({ width: 0, height: 0 });

  const color = getNodeTypeColor(nodeType);
  const icon = getNodeTypeIcon(nodeType);
  const typeLabel = getNodeTypeLabel(nodeType);

  // Determine if this is a group node (has children) or end node (has finding_ids)
  // Check both children array (nested format) and childCount (flat format)
  const hasChildrenArray = Array.isArray(children) && children.length > 0;
  const hasChildren = hasChildrenArray || (childCount !== undefined && childCount > 0);
  const hasFindingIds = Array.isArray(findingIds) && findingIds.length > 0;
  const displayChildCount = childCount ?? (hasChildrenArray ? children.length : 0);
  const displayFindingCount = findingCount ?? (hasFindingIds ? findingIds.length : 0);

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent node selection
    setIsLocalExpanded(!isLocalExpanded);
    // Also notify parent if callback provided
    if (onToggleExpand) {
      onToggleExpand(id);
    }
  };

  const handleFindingClick = (e: React.MouseEvent, findingId: string) => {
    e.stopPropagation();
    if (onFindingClick) {
      onFindingClick(findingId);
    }
  };

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    isResizing.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    startDimensions.current = { width: dimensions.width, height: dimensions.height };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing.current) return;
      const deltaX = moveEvent.clientX - startPos.current.x;
      const deltaY = moveEvent.clientY - startPos.current.y;
      setDimensions({
        width: Math.max(350, startDimensions.current.width + deltaX),
        height: Math.max(250, startDimensions.current.height + deltaY),
      });
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [dimensions]);

  // Calculate expanded size - use custom dimensions if resized
  const expandedWidth = dimensions.width;
  const expandedHeight = dimensions.height;

  return (
    <div
      className={`
        bg-surface rounded-lg border-2 shadow-md
        transition-all duration-300 ease-in-out
        ${isExpanded && hasChildren ? '' : 'min-w-[220px] max-w-[300px]'}
        ${selected ? 'ring-2 ring-accent ring-offset-2' : ''}
      `}
      style={{
        borderColor: selected ? color : 'var(--border-default)',
        width: isExpanded && hasChildren ? expandedWidth : undefined,
        minHeight: isExpanded && hasChildren ? expandedHeight : undefined,
      }}
    >
      {/* Input Handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
      />

      {/* Header with badge and expand control */}
      <div
        className="px-3 py-2 rounded-t-md flex items-center gap-2"
        style={{ backgroundColor: `${color}15` }}
      >
        {/* Expand/Collapse Button (only for group nodes) */}
        {hasChildren && (
          <button
            onClick={handleExpandClick}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-surface/50 transition-colors text-fg-secondary"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}

        {/* Type Badge */}
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        {/* Type Label */}
        <span
          className="text-xs font-medium uppercase tracking-wide"
          style={{ color }}
        >
          {typeLabel}
        </span>

        {/* Child/Finding Count Badge */}
        {(displayChildCount > 0 || displayFindingCount > 0) && (
          <span className="ml-auto text-xs text-fg-tertiary bg-surface px-1.5 py-0.5 rounded flex items-center gap-1">
            {displayChildCount > 0 && (
              <span title={formatCount(displayChildCount, { one: 'child node', other: 'child nodes' })}>
                {formatCount(displayChildCount, { one: 'item', other: 'items' })}
              </span>
            )}
            {displayFindingCount > 0 && displayChildCount > 0 && (
              <span className="text-fg-secondary">|</span>
            )}
            {displayFindingCount > 0 && (
              <span title={formatCount(displayFindingCount, { one: 'linked finding', other: 'linked findings' })}>
                {formatCount(displayFindingCount, { one: 'finding', other: 'findings' })}
              </span>
            )}
          </span>
        )}

        {/* Reference Count (fallback if no children/findings) */}
        {displayChildCount === 0 && displayFindingCount === 0 && referenceIds.length > 0 && (
          <span className="ml-auto text-xs text-fg-tertiary bg-surface px-1.5 py-0.5 rounded">
            {formatCount(referenceIds.length, { one: 'ref', other: 'refs' })}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        {/* Label */}
        <h3 className="text-sm font-semibold text-fg mb-1 line-clamp-2">
          {label}
        </h3>

        {/* Content/Relevance Note */}
        {(content || relevanceNote) && (
          <p className={`text-xs text-fg-secondary ${isExpanded ? '' : 'line-clamp-3'}`}>
            {content || relevanceNote}
          </p>
        )}

        {/* Expansion indicator for group nodes - COLLAPSED STATE */}
        {hasChildren && !isExpanded && (
          <div className="mt-2 pt-2 border-t border-divider">
            <button
              onClick={handleExpandClick}
              className="text-xs text-node-definition hover:text-node-definition font-medium flex items-center gap-1 transition-colors"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Show {formatCount(displayChildCount, { one: 'sub-item', other: 'sub-items' })}
            </button>
          </div>
        )}

        {/* Finding links for END nodes */}
        {hasFindingIds && !hasChildren && (
          <div className="mt-2 pt-2 border-t border-divider">
            <div className="text-xs text-fg-tertiary mb-1.5 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              {formatCount(displayFindingCount, { one: 'linked finding', other: 'linked findings' })}
            </div>
            <div className="flex flex-wrap gap-1">
              {findingIds!.slice(0, 4).map((findingId) => (
                <button
                  key={findingId}
                  onClick={(e) => handleFindingClick(e, findingId)}
                  className="px-2 py-0.5 text-[10px] bg-success-bg text-success rounded hover:bg-success-bg transition-colors font-medium"
                >
                  {findingId}
                </button>
              ))}
              {findingIds!.length > 4 && (
                <span className="px-2 py-0.5 text-[10px] text-fg-tertiary">
                  +{findingIds!.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* EXPANDED: Internal Sub-Graph for GROUP nodes (only for nested format with children array) */}
      {isExpanded && hasChildrenArray && (
        <div className="px-3 pb-3 relative">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-node-definition">Sub-graph</span>
            <button
              onClick={handleExpandClick}
              className="text-xs text-fg-tertiary hover:text-fg-secondary transition-colors"
            >
              Collapse
            </button>
          </div>
          <div
            className="border border-border-default rounded-lg bg-bg-warm overflow-hidden"
            style={{ height: dimensions.height - 110 }}
          >
            <InternalGraph
              children={children!}
              childEdges={childEdges}
              onFindingClick={onFindingClick}
            />
          </div>

          {/* Resize Handle - bottom right corner */}
          <div
            className="absolute bottom-1 right-1 w-4 h-4 cursor-se-resize opacity-50 hover:opacity-100 transition-opacity"
            onMouseDown={handleResizeStart}
            title="Drag to resize"
          >
            <svg viewBox="0 0 24 24" className="w-full h-full text-fg-tertiary">
              <path
                fill="currentColor"
                d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Output Handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-fg-tertiary border-2 border-white"
      />
    </div>
  );
}

export default memo(ExpandableNode);
